import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { MemberAvatar } from "../components/MemberFormPieces";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ChatCircleDots, Buildings, User, UsersThree, PaperPlaneRight,
  Plus, X, MagnifyingGlass, Trash,
} from "@phosphor-icons/react";

const KIND_META = {
  project: { icon: Buildings, color: "text-orange-600", label: "Project" },
  group:   { icon: UsersThree, color: "text-blue-600", label: "Group" },
  dm:      { icon: User, color: "text-emerald-700", label: "DM" },
};

function timeStr(iso) {
  if (!iso) return "";
  const d = new Date(iso); if (isNaN(d)) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function ChatPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [rooms, setRooms] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [pendingDeleteRoom, setPendingDeleteRoom] = useState(null);
  const [pendingDeleteMsg, setPendingDeleteMsg] = useState(null);
  const listRef = useRef(null);

  const active = useMemo(() => rooms.find((r) => r.id === activeId), [rooms, activeId]);

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await http.get("/chat/rooms");
      setRooms(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [activeId]);

  const loadMessages = useCallback(async (rid) => {
    if (!rid) return;
    try {
      const { data } = await http.get(`/chat/rooms/${rid}/messages?limit=100`);
      setMessages(data);
      await http.post(`/chat/rooms/${rid}/read`);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const doDeleteRoom = async () => {
    if (!pendingDeleteRoom) return;
    try {
      await http.delete(`/chat/rooms/${pendingDeleteRoom.id}`);
      toast.success(`Chat "${pendingDeleteRoom.name || 'DM'}" deleted`);
      if (activeId === pendingDeleteRoom.id) setActiveId(null);
      setPendingDeleteRoom(null);
      await loadRooms();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDeleteMessage = async () => {
    if (!pendingDeleteMsg || !activeId) return;
    try {
      await http.delete(`/chat/rooms/${activeId}/messages/${pendingDeleteMsg.id}`);
      setPendingDeleteMsg(null);
      await loadMessages(activeId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { loadRooms(); }, [loadRooms]);
  useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);

  // Poll active room every 3s
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => loadMessages(activeId), 3000);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  // Poll rooms list every 8s for unread updates
  useEffect(() => {
    const t = setInterval(() => loadRooms(), 8000);
    return () => clearInterval(t);
  }, [loadRooms]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !activeId) return;
    setSending(true);
    try {
      const { data } = await http.post(`/chat/rooms/${activeId}/messages`, { text: t });
      setMessages((prev) => [...prev, data]);
      setText("");
      loadRooms();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.project || "").toLowerCase().includes(q) ||
      (r.last_message?.text || "").toLowerCase().includes(q)
    );
  }, [rooms, search]);

  const grouped = useMemo(() => ({
    project: filtered.filter((r) => r.kind === "project"),
    group:   filtered.filter((r) => r.kind === "group"),
    dm:      filtered.filter((r) => r.kind === "dm"),
  }), [filtered]);

  return (
    <Layout
      title="Team Chat"
      subtitle="Internal channels for projects, custom groups & one-to-one conversations. Not visible to clients."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-0 border border-zinc-300 bg-white h-[calc(100vh-220px)] min-h-[500px] overflow-hidden"
           data-testid="chat-page">
        {/* Left rail: rooms — independent scroll */}
        <aside className="border-r border-zinc-300 flex flex-col bg-zinc-50 h-full min-h-0 overflow-hidden">
          <div className="p-3 border-b border-zinc-300 bg-white flex gap-2 shrink-0">
            <div className="relative flex-1">
              <MagnifyingGlass size={13} weight="bold"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search chats…" data-testid="chat-search-input"
                className="w-full h-9 pl-8 pr-2 border border-zinc-300 bg-white text-xs focus:outline-none focus:border-blue-600"
              />
            </div>
            <button onClick={() => setStarting(true)} data-testid="new-dm-btn"
              className="rounded-none h-9 flex items-center justify-center border border-emerald-700 bg-emerald-50 text-emerald-800 hover:bg-emerald-700 hover:text-white px-2.5"
              title="New Direct Message">
              <User size={13} weight="bold" />
            </button>
            {isAdmin && (
              <button onClick={() => setCreating(true)} data-testid="new-group-btn"
                className="rounded-none h-9 flex items-center justify-center border border-orange-600 bg-orange-50 text-orange-800 hover:bg-orange-600 hover:text-white px-2.5"
                title="New Group">
                <UsersThree size={13} weight="bold" />
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="chat-rooms-scroll">
            <RoomList label="Direct Messages" items={grouped.dm} activeId={activeId} onPick={setActiveId} kind="dm" isAdmin={isAdmin} onDelete={setPendingDeleteRoom} />
            <RoomList label="Project Rooms" items={grouped.project} activeId={activeId} onPick={setActiveId} kind="project" isAdmin={isAdmin} onDelete={setPendingDeleteRoom} />
            <RoomList label="Groups" items={grouped.group} activeId={activeId} onPick={setActiveId} kind="group" isAdmin={isAdmin} onDelete={setPendingDeleteRoom} />
            {rooms.length === 0 && (
              <div className="p-6 text-center label-overline text-zinc-400" data-testid="no-rooms">
                No chats yet
              </div>
            )}
          </div>
        </aside>

        {/* Right: active conversation — independent scroll */}
        <section className="flex flex-col h-full min-h-0 overflow-hidden" data-testid="chat-panel">
          {!active && (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <ChatCircleDots size={40} weight="bold" className="mx-auto text-zinc-300" />
                <div className="label-overline text-zinc-500 mt-3">Pick a chat to start</div>
              </div>
            </div>
          )}

          {active && (
            <>
              <header className="px-4 sm:px-5 py-3 border-b border-zinc-300 bg-zinc-50 shrink-0" data-testid="chat-header">
                <div className="flex items-center gap-3">
                  {(() => {
                    const K = KIND_META[active.kind]; const I = K.icon;
                    return <I size={16} weight="bold" className={K.color} />;
                  })()}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black tracking-tighter text-zinc-900 truncate">
                      {active.name || "Direct Message"}
                    </div>
                    <div className="label-overline text-zinc-500 truncate">
                      {KIND_META[active.kind].label} · {active.members.length} member{active.members.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => setPendingDeleteRoom(active)}
                      data-testid="delete-active-room-btn"
                      className="shrink-0 label-overline border border-red-300 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white px-2.5 py-1.5 flex items-center gap-1"
                      title="Delete chat"
                    >
                      <Trash size={11} weight="bold" /> Delete
                    </button>
                  )}
                </div>
                {active.kind !== "dm" && active.member_names && active.member_names.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="chat-header-members">
                    {active.member_names.map((n, i) => (
                      <span key={`${n}-${i}`}
                            data-testid={`chat-member-chip-${i}`}
                            className="inline-flex items-center gap-1 label-overline bg-white border border-zinc-300 text-zinc-700 px-2 py-0.5"
                            title={n}>
                        <User size={9} weight="bold" className="text-zinc-500" /> {n}
                      </span>
                    ))}
                  </div>
                )}
              </header>

              <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2.5 bg-zinc-50" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center label-overline text-zinc-400 py-10">
                    No messages yet — say hi
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === me?.id;
                  return (
                    <div key={m.id} data-testid={`chat-msg-${m.id}`}
                         className={`group flex items-end gap-1.5 ${mine ? "justify-end" : "justify-start"}`}>
                      {isAdmin && mine && (
                        <button
                          onClick={() => setPendingDeleteMsg(m)}
                          data-testid={`delete-msg-${m.id}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-zinc-400 hover:text-red-600"
                          title="Delete message"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                      <div className={`max-w-[85%] sm:max-w-[70%] ${mine ? "bg-blue-600 text-white" : "bg-white text-zinc-900 border border-zinc-300"} px-3 py-2 rounded-none`}>
                        {!mine && <div className="label-overline text-zinc-500 mb-0.5">{m.sender_name}</div>}
                        <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`text-[10px] font-mono mt-1 ${mine ? "text-blue-100" : "text-zinc-500"} text-right`}>
                          {timeStr(m.created_at)}
                        </div>
                      </div>
                      {isAdmin && !mine && (
                        <button
                          onClick={() => setPendingDeleteMsg(m)}
                          data-testid={`delete-msg-${m.id}`}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-zinc-400 hover:text-red-600"
                          title="Delete message"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="border-t border-zinc-300 p-3 flex gap-2 bg-white shrink-0" data-testid="chat-composer">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  data-testid="chat-input"
                  className="rounded-none border-zinc-400 focus-visible:ring-blue-600 focus-visible:ring-offset-0 flex-1"
                  maxLength={4000}
                />
                <Button
                  type="submit" disabled={sending || !text.trim()} data-testid="chat-send"
                  className="rounded-none h-10 bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs px-4"
                >
                  <PaperPlaneRight size={13} weight="bold" className="mr-1" />
                  {sending ? "…" : "Send"}
                </Button>
              </form>
            </>
          )}
        </section>
      </div>

      {isAdmin && (
        <NewGroupDialog open={creating} onOpenChange={setCreating} onCreated={(id) => { loadRooms(); setActiveId(id); }} />
      )}
      <StartDmDialog open={starting} onOpenChange={setStarting} me={me} onOpened={(id) => { loadRooms(); setActiveId(id); }} />

      <AlertDialog open={!!pendingDeleteRoom} onOpenChange={(o) => !o && setPendingDeleteRoom(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Delete Chat</div>
            <AlertDialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
              Delete &quot;{pendingDeleteRoom?.name || 'Direct Message'}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              All messages in this chat will be permanently removed for every member. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteRoom}
              data-testid="confirm-delete-room"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs"
            >Yes, Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingDeleteMsg} onOpenChange={(o) => !o && setPendingDeleteMsg(null)}>
        <AlertDialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal">
          <AlertDialogHeader>
            <div className="label-overline text-red-600 mb-1">Delete Message</div>
            <AlertDialogTitle className="font-display text-xl font-black tracking-tighter uppercase">Delete this message?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will remove the message for every member of the chat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeleteMessage}
              data-testid="confirm-delete-msg"
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono uppercase tracking-widest text-xs"
            >Yes, Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function RoomList({ label, items, activeId, onPick, kind, isAdmin, onDelete }) {
  if (items.length === 0) return null;
  const K = KIND_META[kind]; const Icon = K.icon;
  return (
    <div>
      <div className="px-4 py-2 label-overline text-zinc-500 flex items-center gap-1.5 bg-zinc-100/70 border-b border-zinc-200">
        <Icon size={11} weight="bold" className={K.color} />
        {label} <span className="ml-auto">{items.length}</span>
      </div>
      {items.map((r) => (
        <div key={r.id} className={`group relative border-b border-zinc-200 ${activeId === r.id ? "bg-white border-l-4 border-l-blue-600" : "hover:bg-white"}`}>
          <button
                  data-testid={`chat-room-${r.id}`}
                  onClick={() => onPick(r.id)}
                  className="w-full text-left px-4 py-3 pr-9">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-zinc-900 truncate">{r.name || "Direct Message"}</span>
                  {r.unread > 0 && (
                    <span data-testid={`chat-unread-${r.id}`} className="label-overline bg-orange-600 text-white px-1.5 py-0.5">{r.unread}</span>
                  )}
              </div>
              {r.last_message?.text && (
                <div className="text-xs text-zinc-500 truncate mt-0.5">
                  <span className="font-mono">{r.last_message.sender_name}:</span> {r.last_message.text}
                </div>
              )}
            </div>
            <span className="label-overline text-zinc-400 font-mono shrink-0">{timeStr(r.last_message_at)}</span>
          </div>
        </button>
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete?.(r); }}
              data-testid={`delete-room-${r.id}`}
              title="Delete chat"
              className="absolute top-1/2 right-2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 text-zinc-400 hover:text-red-600 bg-white/80 rounded-none border border-zinc-200"
            >
              <Trash size={12} weight="bold" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function NewGroupDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setPicked(new Set());
    http.get("/users").then((r) => setStaff(r.data)).catch(() => {});
  }, [open]);

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    if (picked.size < 1) { toast.error("Pick at least 1 additional member"); return; }
    setSaving(true);
    try {
      const { data } = await http.post("/chat/rooms", { name, member_ids: [...picked] });
      toast.success(`Group "${data.name}" created`);
      onOpenChange(false);
      onCreated?.(data.id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="label-overline text-orange-600 mb-1">New Chat Group</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">Create Group</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="new-group-form">
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Group Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120}
                   data-testid="group-name"
                   className="rounded-none border-zinc-400 focus-visible:ring-orange-600 focus-visible:ring-offset-0" />
          </div>
          <div>
            <label className="label-overline block mb-1 text-zinc-700">Members *</label>
            <div className="border border-zinc-300 max-h-64 overflow-y-auto">
              {staff.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer">
                  <input type="checkbox" checked={picked.has(u.id)}
                         onChange={() => toggle(u.id)}
                         data-testid={`group-member-${u.id}`} />
                  <MemberAvatar user={u} size={24} className="rounded-none" />
                  <span className="text-sm">{u.name}</span>
                  <span className="ml-auto label-overline text-zinc-500">{u.role}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="submit-group"
                    className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
              {saving ? "Creating…" : "Create Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StartDmDialog({ open, onOpenChange, me, onOpened }) {
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return; setQ("");
    http.get("/users").then((r) => setStaff(r.data.filter((u) => u.id !== me?.id))).catch(() => {});
  }, [open, me?.id]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? staff.filter((u) => u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s)) : staff;
  }, [staff, q]);
  const openDm = async (uid) => {
    try {
      const { data } = await http.post(`/chat/rooms/dm/${uid}`);
      toast.success(`Chat opened with ${data.name}`);
      onOpenChange(false);
      onOpened?.(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="label-overline text-emerald-700 mb-1">Direct Message</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">Start DM</DialogTitle>
        </DialogHeader>
        <Input placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)}
               data-testid="dm-search"
               className="rounded-none border-zinc-400 my-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-0" />
        <div className="border border-zinc-300 max-h-72 overflow-y-auto">
          {list.map((u) => (
            <button key={u.id}
                    onClick={() => openDm(u.id)}
                    data-testid={`dm-target-${u.id}`}
                    className="w-full text-left px-3 py-2 border-b border-zinc-200 hover:bg-zinc-50 flex items-center gap-3">
              <MemberAvatar user={u} size={32} className="rounded-none" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-zinc-900 truncate">{u.name}</div>
                <div className="text-xs text-zinc-500 truncate">{u.email}</div>
              </div>
              <span className="label-overline text-zinc-400">{u.role}</span>
            </button>
          ))}
          {list.length === 0 && <div className="p-6 text-center label-overline text-zinc-400">No matches</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
